import React, { useState, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormControlLabel, Checkbox } from '@material-ui/core';
import c from 'classnames';
import { getStaticText } from '../../../utils/constants';
import { getReportDynamicTextInfo, mapPsOhiBuildReportOptions } from '../reportPublicSector';
import {
    BUILD_REPORT_PAYLOAD,
    BUILD_REPORT_PULSE_PAYLOAD,
    BUILD_REPORT_EX_PAYLOAD_DISABLED,
    BUILD_REPORT_ORGANIZATIONAL_VALUES_PAYLOAD_DISABLED,
    AI_ORA_HORIZON_HEATMAP_KEY,
    AI_ORA_QUESTION_SLIDES_5_POINTER_SCALE_KEY,
    getBuildReportPayloadAiOra,
    getBuildReportOptionAiOra,
    mergeAiOraBuildYourReport,
} from '../constants';
import './index.scss';

function BuildReport({ defaultSettings, goBack, submitReport, reportApiParams, reportIndex, compYrSettings }) {
    const {
        lang: defaultLang,
        ohi_text,
        survey_type = '',
        report_type = '',
        emp_exp_resurvey_settings = '',
        apply_employee_exp_benchmark = false,
        is_employee_experience = true,
        is_values_module = false,
        enable_pulse_ranking = false,
        is_public_sector_ohi = false,
        enable_ai_ora_horizon_heatmap: enableDemographicHorizonHeatmaps,
        enable_five_point_qbyq: enableFivePointQbyq,
    } = defaultSettings;
    const { compYrSettings: settings = {}, settingsFetched = false } = compYrSettings.compYr0 || {};
    const { apply_employee_exp_benchmark: compYrApplyExBm = false } = settings || {};
    const staticText = getStaticText(defaultLang);
    const { SITE_TEXT } = staticText;
    const { BACK, NEXT, DYNAMIC_TEXT_INFO, BUILD_REPORT, BUILD_TEXT, BUILD_REPORT_OPTION, ADDITIONAL_PRACTICES } =
        SITE_TEXT || {};
    const { REPORT_TYPE } = getReportDynamicTextInfo(DYNAMIC_TEXT_INFO, { ohi_text, is_public_sector_ohi });
    const isAiOraReport = reportIndex === 10;
    const aiOraModuleRaw = defaultSettings?.survey_meta_config?.ai_ora_module_type?.value;
    const defaultPayload = isAiOraReport ? getBuildReportPayloadAiOra(aiOraModuleRaw) : BUILD_REPORT_PAYLOAD;
    const baseBuildReportOptions =
        (isAiOraReport ? getBuildReportOptionAiOra(aiOraModuleRaw) : BUILD_REPORT_OPTION) || [];
    const shouldShowDemographicHorizonHeatmaps =
        isAiOraReport &&
        enableDemographicHorizonHeatmaps === true &&
        baseBuildReportOptions.some(({ key }) => key === AI_ORA_HORIZON_HEATMAP_KEY);
    const shouldShowQuestionLevelResults5Pt =
        isAiOraReport &&
        enableFivePointQbyq === true &&
        baseBuildReportOptions.some(({ key }) => key === AI_ORA_QUESTION_SLIDES_5_POINTER_SCALE_KEY);
    const optionalAiOraReportKeys = [
        { key: AI_ORA_HORIZON_HEATMAP_KEY, shouldShow: shouldShowDemographicHorizonHeatmaps },
        { key: AI_ORA_QUESTION_SLIDES_5_POINTER_SCALE_KEY, shouldShow: shouldShowQuestionLevelResults5Pt },
    ];
    const getAiOraPayloadInUse = payloadInUse => {
        const nextPayload = { ...payloadInUse };
        optionalAiOraReportKeys.forEach(({ key, shouldShow }) => {
            if (shouldShow) {
                if (!nextPayload[key]) {
                    nextPayload[key] = { keep: true };
                }
            } else {
                delete nextPayload[key];
            }
        });
        return nextPayload;
    };
    const buildReportOptions = useMemo(() => {
        const mappedOptions = mapPsOhiBuildReportOptions(
            baseBuildReportOptions,
            is_public_sector_ohi,
            reportIndex,
            ADDITIONAL_PRACTICES
        );
        const hiddenKeys = optionalAiOraReportKeys.filter(({ shouldShow }) => !shouldShow).map(({ key }) => key);
        if (!hiddenKeys.length) return mappedOptions;
        return mappedOptions.filter(({ key }) => !hiddenKeys.includes(key));
    }, [
        baseBuildReportOptions,
        is_public_sector_ohi,
        reportIndex,
        ADDITIONAL_PRACTICES,
        shouldShowDemographicHorizonHeatmaps,
        shouldShowQuestionLevelResults5Pt,
    ]);

    const { build_your_report: savedBuildYourReport } = reportApiParams || {};
    const [payload, setPayload] = useState(() =>
        isAiOraReport
            ? getAiOraPayloadInUse(mergeAiOraBuildYourReport(savedBuildYourReport, aiOraModuleRaw))
            : savedBuildYourReport || defaultPayload
    );

    const isActive = Object.keys(payload).filter(key => payload[key] && payload[key].keep).length > 0;

    const handleNext = () => {
        reportApiParams.build_your_report = isAiOraReport ? getAiOraPayloadInUse(payload) : payload;
        submitReport(reportApiParams);
    };

    return (
        <div data-testid="BuildReportsComponent" className="buildReportsComponent">
            <div className="generalDemoSection">
                <h2>
                    {REPORT_TYPE[reportIndex]} - {BUILD_REPORT}
                </h2>
                <span className="title">{BUILD_TEXT}</span>
            </div>
            <section className="generalSection">
                <div className="optionWrappered">
                    {buildReportOptions.map(({ key = '', children = [], label, disabled_ids }) => {
                        const { keep } = payload[key] || {};
                        if (children.length) {
                            const hasNestedGroups = children.some(c => c.children && c.children.length > 0);
                            return (
                                <Fragment key={label}>
                                    <div className="sectionLabel">{label}</div>
                                    {hasNestedGroups ? (
                                        <div className="buildReportSectionChildren">
                                            {children.map((child, childIndex) => {
                                                const hasNestedChildren = child.children && child.children.length > 0;
                                                if (hasNestedChildren) {
                                                    const parentKey = child.key;
                                                    const parentKeep = payload[parentKey];
                                                    const parentDisabledIds = child.disabled_ids || [];
                                                    return (
                                                        <div
                                                            key={child.label || childIndex}
                                                            className="buildReportGroup"
                                                        >
                                                            <FormControlLabel
                                                                classes={{
                                                                    root: 'innerReportCheckbox groupParentCheckbox',
                                                                }}
                                                                key={parentKey}
                                                                disabled={parentDisabledIds.includes(reportIndex)}
                                                                control={
                                                                    <Checkbox
                                                                        checked={parentKeep && parentKeep.keep}
                                                                        onChange={({ target }) => {
                                                                            const { checked: value } = target;
                                                                            setPayload({
                                                                                ...payload,
                                                                                [parentKey]: { keep: value },
                                                                            });
                                                                        }}
                                                                    />
                                                                }
                                                                label={
                                                                    <span>
                                                                        {child.label}
                                                                        {child.showInfo && (
                                                                            <span
                                                                                className="reportOptionInfoIcon"
                                                                                title="More information"
                                                                                role="img"
                                                                                aria-label="info"
                                                                            >
                                                                                ℹ
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                }
                                                                labelPlacement="end"
                                                            />
                                                            {child.children.map(
                                                                ({
                                                                    key: innerKey,
                                                                    label: innerLabel,
                                                                    disabled_ids: disabledIds = [],
                                                                    showInfo,
                                                                }) => {
                                                                    const { keep: innerKeep } = payload[innerKey];
                                                                    const isPulseDisabledOp = !!BUILD_REPORT_PULSE_PAYLOAD[
                                                                        innerKey
                                                                    ];
                                                                    const isExDisabledOp = !!BUILD_REPORT_EX_PAYLOAD_DISABLED[
                                                                        innerKey
                                                                    ];
                                                                    const isOrgValuesDisabledOp = !!BUILD_REPORT_ORGANIZATIONAL_VALUES_PAYLOAD_DISABLED[
                                                                        innerKey
                                                                    ];
                                                                    return (
                                                                        <FormControlLabel
                                                                            classes={{
                                                                                root:
                                                                                    'innerReportCheckbox innerReportCheckboxNested',
                                                                            }}
                                                                            key={innerKey}
                                                                            disabled={
                                                                                disabledIds.includes(reportIndex) ||
                                                                                (survey_type === 'Pulse' &&
                                                                                    isPulseDisabledOp &&
                                                                                    !enable_pulse_ranking) ||
                                                                                (!is_employee_experience &&
                                                                                    isExDisabledOp) ||
                                                                                (!is_values_module &&
                                                                                    isOrgValuesDisabledOp)
                                                                            }
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={innerKeep}
                                                                                    onChange={({ target }) => {
                                                                                        const {
                                                                                            checked: value,
                                                                                        } = target;
                                                                                        setPayload({
                                                                                            ...payload,
                                                                                            [innerKey]: {
                                                                                                keep: value,
                                                                                            },
                                                                                        });
                                                                                    }}
                                                                                />
                                                                            }
                                                                            label={
                                                                                <span>
                                                                                    {innerLabel}
                                                                                    {showInfo && (
                                                                                        <span
                                                                                            className="reportOptionInfoIcon"
                                                                                            title="More information"
                                                                                            role="img"
                                                                                            aria-label="info"
                                                                                        >
                                                                                            ℹ
                                                                                        </span>
                                                                                    )}
                                                                                </span>
                                                                            }
                                                                            labelPlacement="end"
                                                                        />
                                                                    );
                                                                }
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                const {
                                                    key: innerKey,
                                                    label: innerLabel,
                                                    disabled_ids: disabledIds = [],
                                                    showInfo,
                                                } = child;
                                                const { keep: innerKeep } = payload[innerKey];
                                                const isPulseDisabledOp = !!BUILD_REPORT_PULSE_PAYLOAD[innerKey];
                                                const isExDisabledOp = !!BUILD_REPORT_EX_PAYLOAD_DISABLED[innerKey];
                                                const isOrgValuesDisabledOp = !!BUILD_REPORT_ORGANIZATIONAL_VALUES_PAYLOAD_DISABLED[
                                                    innerKey
                                                ];
                                                return (
                                                    <FormControlLabel
                                                        classes={{
                                                            root: 'innerReportCheckbox',
                                                        }}
                                                        key={innerKey}
                                                        disabled={
                                                            disabledIds.includes(reportIndex) ||
                                                            (survey_type === 'Pulse' &&
                                                                isPulseDisabledOp &&
                                                                !enable_pulse_ranking) ||
                                                            (!is_employee_experience && isExDisabledOp) ||
                                                            (!is_values_module && isOrgValuesDisabledOp)
                                                        }
                                                        control={
                                                            <Checkbox
                                                                checked={innerKeep}
                                                                onChange={({ target }) => {
                                                                    const { checked: value } = target;
                                                                    setPayload({
                                                                        ...payload,
                                                                        [innerKey]: {
                                                                            keep: value,
                                                                        },
                                                                    });
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <span>
                                                                {innerLabel}
                                                                {showInfo && (
                                                                    <span
                                                                        className="reportOptionInfoIcon"
                                                                        title="More information"
                                                                        role="img"
                                                                        aria-label="info"
                                                                    >
                                                                        ℹ
                                                                    </span>
                                                                )}
                                                            </span>
                                                        }
                                                        labelPlacement="end"
                                                    />
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        children.map((child, childIndex) => {
                                            const {
                                                key: innerKey,
                                                label: innerLabel,
                                                disabled_ids: disabledIds = [],
                                                showInfo,
                                            } = child;
                                            const { keep: innerKeep } = payload[innerKey];
                                            const isPulseDisabledOp = !!BUILD_REPORT_PULSE_PAYLOAD[innerKey];
                                            const isExDisabledOp = !!BUILD_REPORT_EX_PAYLOAD_DISABLED[innerKey];
                                            const isOrgValuesDisabledOp = !!BUILD_REPORT_ORGANIZATIONAL_VALUES_PAYLOAD_DISABLED[
                                                innerKey
                                            ];
                                            return (
                                                <FormControlLabel
                                                    classes={{
                                                        root: 'innerReportCheckbox',
                                                    }}
                                                    key={innerKey}
                                                    disabled={
                                                        disabledIds.includes(reportIndex) ||
                                                        (survey_type === 'Pulse' &&
                                                            isPulseDisabledOp &&
                                                            !enable_pulse_ranking) ||
                                                        (!is_employee_experience && isExDisabledOp) ||
                                                        (!is_values_module && isOrgValuesDisabledOp)
                                                    }
                                                    control={
                                                        <Checkbox
                                                            checked={innerKeep}
                                                            onChange={({ target }) => {
                                                                const { checked: value } = target;
                                                                setPayload({
                                                                    ...payload,
                                                                    [innerKey]: {
                                                                        keep: value,
                                                                    },
                                                                });
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <span>
                                                            {innerLabel}
                                                            {showInfo && (
                                                                <span
                                                                    className="reportOptionInfoIcon"
                                                                    title="More information"
                                                                    role="img"
                                                                    aria-label="info"
                                                                >
                                                                    ℹ
                                                                </span>
                                                            )}
                                                        </span>
                                                    }
                                                    labelPlacement="end"
                                                />
                                            );
                                        })
                                    )}
                                </Fragment>
                            );
                        }
                        return (
                            <FormControlLabel
                                classes={{
                                    root: 'reportCheckbox',
                                }}
                                key={key}
                                disabled={disabled_ids.includes(reportIndex)}
                                control={
                                    <Checkbox
                                        name="includeOhi4Demo"
                                        checked={!!keep}
                                        onChange={({ target }) => {
                                            const { checked: value } = target;
                                            setPayload({
                                                ...payload,
                                                [key]: {
                                                    keep: value,
                                                },
                                            });
                                        }}
                                    />
                                }
                                label={<span>{label}</span>}
                                labelPlacement="end"
                            />
                        );
                    })}
                </div>
                <div className="buttonWrapperReports">
                    <div className="outlineBtn" onClick={goBack}>
                        {BACK}
                    </div>
                    <div className={c('blueButton', { active: isActive })} onClick={handleNext}>
                        {NEXT}
                    </div>
                </div>
            </section>
        </div>
    );
}

BuildReport.propTypes = {
    defaultSettings: PropTypes.object.isRequired,
    goBack: PropTypes.func.isRequired,
    submitReport: PropTypes.func.isRequired,
    reportApiParams: PropTypes.object.isRequired,
    reportIndex: PropTypes.number.isRequired,
    compYrSettings: PropTypes.object.isRequired,
};

export default BuildReport;
